import { Person as PersonIcon } from '@mui/icons-material';
import { Box, CircularProgress, Divider, Paper, Stack } from '@mui/material';
import BackendService from 'BackendService';
import React, { useEffect, useRef, useState } from 'react';
import Contact from '../../models/Contact';
import MessageToast from '../MessageToast';
import SearchResultItem from '../common/SearchResultItem';
import { StyledButton, StyledButtonGroup } from '../styles/StyledButtons';
import { StyledInput } from '../styles/StyledInputs';
import { StyledHeading } from '../styles/styles';

interface AddInvestorPanelProps {
  fundOpportunityId: string;
  onCancel: () => void;
  onComplete: () => void;
}

const FORM_ERROR_SUMMARY =
  'Unable to create contact due to missing or invalid fields, see form for errors';
const DUPLICATE_MSG = 'A contact already exists with this email address';
const PREFIX = 'Record already exists with:';

function parseDuplicateFieldsFromError(errorString: string): string[] {
  const part = errorString.split(PREFIX)[1];
  if (!part) return [];
  return part
    .trim()
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function getStructuredErrorMessage(
  error: any,
  action: 'create contact' | 'add contact to opportunity' | 'search contacts',
): { message: string; fieldErrors: Record<string, string> } {
  const fieldErrors: Record<string, string> = {};
  let message = `Unable to ${action} due to an unexpected error, please contact support if problem persists`;

  let duplicateFields: string[] | undefined = error?.duplicateFields;
  if (!duplicateFields && error?.code === 'DUPLICATE_RECORD' && typeof error?.error === 'string') {
    duplicateFields = parseDuplicateFieldsFromError(error.error);
  }

  if (error?.missingFields) {
    error.missingFields.forEach((field: string) => {
      fieldErrors[field] = 'This field is required';
    });
    message = FORM_ERROR_SUMMARY;
  } else if (duplicateFields?.length) {
    duplicateFields.forEach((field: string) => {
      fieldErrors[field] = DUPLICATE_MSG;
    });
    message = FORM_ERROR_SUMMARY;
  } else if (error?.invalidFields) {
    Object.assign(fieldErrors, error.invalidFields);
    message = FORM_ERROR_SUMMARY;
  } else if (error?.structuredError && error?.error) {
    message = error.error;
  } else if (typeof error?.error === 'string') {
    message = error.error;
  } else if (typeof error === 'string') {
    message = error;
  } else {
    console.error(`Error ${action}:`, error);
  }

  return { message, fieldErrors };
}

const AddInvestorPanel: React.FC<AddInvestorPanelProps> = ({
  fundOpportunityId,
  onCancel,
  onComplete,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Contact[]>([]);
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', mobile: '' });
  const [message, setMessage] = useState<{ type: 'error' | 'success'; text: string } | undefined>(
    undefined,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({
    firstName: null,
    lastName: null,
    email: null,
    mobile: null,
  });

  useEffect(() => {
    const frame = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (searchTerm.length > 3) {
      setLoading(true);
      new BackendService()
        .searchContact(searchTerm, true)
        .then((contacts: Contact[]) => {
          setSearchResults(contacts);
          setLoading(false);
        })
        .catch((error: any) => {
          const { message: msg } = getStructuredErrorMessage(error, 'search contacts');
          setMessage({ type: 'error', text: msg });
          setLoading(false);
        });
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  useEffect(() => {
    const keys = Object.keys(fieldErrors).filter((k) => fieldErrors[k]);
    if (keys.length > 0) {
      const el = fieldRefs.current[keys[0]];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.querySelector('input')?.focus();
      }
    }
  }, [fieldErrors]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const applyError = (error: any, action: 'create contact' | 'add contact to opportunity') => {
    const { message: msg, fieldErrors: errors } = getStructuredErrorMessage(error, action);
    setMessage({ type: 'error', text: msg });
    setFieldErrors(errors);
    setLoading(false);
  };

  const createInvestor = (contactId: string) => {
    return new BackendService()
      .createInvestor(contactId, fundOpportunityId)
      .then(() => onComplete())
      .catch((error: any) => applyError(error, 'add contact to opportunity'));
  };

  const handleFormSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const { firstName, lastName, email, mobile } = formData;

    if (!firstName || !lastName || !email || !mobile) {
      setMessage({ type: 'error', text: FORM_ERROR_SUMMARY });
      const err: Record<string, string> = {};
      if (!firstName) err.firstName = 'This field is required';
      if (!lastName) err.lastName = 'This field is required';
      if (!email) err.email = 'This field is required';
      if (!mobile) err.mobile = 'This field is required';
      setFieldErrors(err);
      return;
    }

    setLoading(true);
    setMessage(undefined);
    setFieldErrors({});

    new BackendService()
      .createContact(formData)
      .then((contactId: string) => {
        setFormData({ firstName: '', lastName: '', email: '', mobile: '' });
        return createInvestor(contactId);
      })
      .catch((error: any) => applyError(error, 'create contact'));
  };

  return (
    <>
      <Box sx={{ maxWidth: 480 }}>
        <StyledHeading headerVariant="heading2" tone="tone1" sx={{ mb: 3 }}>
          Add Contact
        </StyledHeading>

        <Stack spacing={2}>
          <StyledInput
            fullWidth
            label="Search Contact"
            ref={searchInputRef}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {loading && <CircularProgress size={24} />}

          {!loading && searchResults.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 280, overflowY: 'auto' }}>
              {searchResults.map((contact, idx) => (
                <Box
                  key={contact.id}
                  onClick={() => {
                    setMessage(undefined);
                    setLoading(true);
                    createInvestor(contact.id);
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: 'pointer',
                    borderTop: idx > 0 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <SearchResultItem
                    icon={PersonIcon}
                    label={contact.name}
                    subtitle={contact.email || contact.mobile || undefined}
                  />
                </Box>
              ))}
            </Paper>
          )}

          <Divider />

          <form onSubmit={handleFormSubmit}>
            <StyledHeading headerVariant="subheading" tone="tone1" sx={{ mb: 1.5 }}>
              Create New Contact
            </StyledHeading>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <StyledInput
                  ref={(el) => {
                    fieldRefs.current.firstName = el;
                  }}
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                />
                <StyledInput
                  ref={(el) => {
                    fieldRefs.current.lastName = el;
                  }}
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                />
              </Stack>
              <Stack spacing={2} sx={{ flex: 1 }}>
                <StyledInput
                  ref={(el) => {
                    fieldRefs.current.email = el;
                  }}
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                />
                <StyledInput
                  ref={(el) => {
                    fieldRefs.current.mobile = el;
                  }}
                  label="Mobile"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  fullWidth
                  error={!!fieldErrors.mobile}
                  helperText={fieldErrors.mobile}
                />
              </Stack>
            </Box>
          </form>

          <StyledButtonGroup>
            <StyledButton variant="contained" onClick={() => handleFormSubmit()} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Create Contact'}
            </StyledButton>
            <StyledButton variant="outlined" onClick={onCancel} disabled={loading}>
              Cancel
            </StyledButton>
          </StyledButtonGroup>
        </Stack>
      </Box>

      {message && (
        <MessageToast
          type={message.type}
          message={message.text}
          open={true}
          onClose={() => setMessage(undefined)}
        />
      )}
    </>
  );
};

export default AddInvestorPanel;
